import { doc, getDoc } from 'firebase/firestore';
import { db as firestore } from './firebaseFirestore';

import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// Hybrid backend: load static per-namespace JSON from `/locales/{{lng}}/{{ns}}.json`
// then apply Firestore overrides from `app-configurations/translations` document
// The Firestore structure is flexible: we support both
//   translations: { en: { common: {...}, home: {...} } }
// and a flat-language object: translations: { en: { key: value, ... } }

const deepMerge = (target: any, source: any) => {
  if (!source) return target;
  const out = { ...(target || {}) };
  Object.keys(source).forEach((k) => {
    if (
      source[k] &&
      typeof source[k] === 'object' &&
      !Array.isArray(source[k]) &&
      typeof out[k] === 'object'
    ) {
      out[k] = deepMerge(out[k], source[k]);
    } else {
      out[k] = source[k];
    }
  });
  return out;
};

const getLanguageCandidates = (lng: string) => {
  const normalizedLanguage = typeof lng === 'string' ? lng.trim() : '';
  const baseLanguage = normalizedLanguage.split('-')[0];

  return Array.from(new Set([baseLanguage || normalizedLanguage || 'en'].filter(Boolean)));
};

const jsonRequestCache = new Map<string, Promise<any>>();
const namespaceRequestCache = new Map<string, Promise<any>>();
let firestoreTranslationsRequest: Promise<any> | null = null;

// Optional translation resources must not hold AuthProvider's startup loader open
// indefinitely. Late results are ignored for this session; bundled/default copy
// remains usable when either the HTTP request or Firestore is unreachable.
const withinTranslationDeadline = <T,>(work: Promise<T>, delay: number, fallback: T): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    work,
    new Promise<T>((resolve) => { timer = setTimeout(() => resolve(fallback), delay); }),
  ]).finally(() => clearTimeout(timer));
};

const fetchJson = async (resourcePath: string) => {
  if (jsonRequestCache.has(resourcePath)) {
    return jsonRequestCache.get(resourcePath);
  }

  const request = withinTranslationDeadline((async () => {
  try {
    const res = await fetch(resourcePath, { cache: 'default' });
    if (res && res.status < 400) {
      return await res.json();
    }
  } catch {
    // ignore and fall back
  }

  return null;
  })(), 5000, null);

  jsonRequestCache.set(resourcePath, request);
  return request;
};

const extractNamespaceFromRootJson = (json: any, ns: string) => {
  if (!json || typeof json !== 'object') {
    return {};
  }

  if (json[ns] && typeof json[ns] === 'object') {
    return json[ns];
  }

  if (ns === 'common') {
    return json;
  }

  return {};
};

const fetchStaticNamespace = async (lng: string, ns: string) => {
  let rootNamespaceData = {};
  let namespacedData = {};

  for (const language of getLanguageCandidates(lng)) {
    const rootJson = await fetchJson(`/locales/${language}.json`);
    rootNamespaceData = deepMerge(rootNamespaceData, extractNamespaceFromRootJson(rootJson, ns));

    const namespacedJson = await fetchJson(`/locales/${language}/${ns}.json`);
    if (namespacedJson && typeof namespacedJson === 'object') {
      namespacedData = deepMerge(namespacedData, namespacedJson);
    }
  }

  return deepMerge(rootNamespaceData, namespacedData);
};

const extractFirestoreNamespace = (data: any, lng: string, ns: string) => {
  let mergedData = {};

  for (const language of getLanguageCandidates(lng)) {
    const languageData = data?.[language];
    if (languageData && typeof languageData === 'object') {
      mergedData = deepMerge(
        mergedData,
        languageData[ns] && typeof languageData[ns] === 'object'
          ? languageData[ns]
          : languageData,
      );
    }

    const namespaceData = data?.[ns]?.[language];
    if (namespaceData && typeof namespaceData === 'object') {
      mergedData = deepMerge(mergedData, namespaceData);
    }
  }

  return mergedData;
};

const fetchFirestoreNamespace = async (lng: string, ns: string) => {
  try {
    firestoreTranslationsRequest =
      firestoreTranslationsRequest ||
      withinTranslationDeadline((async () => {
        const docRef = doc(firestore, 'app-configurations', 'translations');
        const snap = await getDoc(docRef);
        return snap.exists() ? snap.data() || {} : {};
      })(), 3000, {});
    const data = await firestoreTranslationsRequest;
    return extractFirestoreNamespace(data, lng, ns);
  } catch {
    return {};
  }
};

// i18next backend-like loader used in `loadNamespace` flows
const loadNamespace = async (lng: string, ns: string) => {
  const cacheKey = `${lng.split?.('-')?.[0] || lng || 'en'}:${ns}`;
  if (namespaceRequestCache.has(cacheKey)) {
    return namespaceRequestCache.get(cacheKey);
  }

  const request = (async () => {
  const [staticRes, firestoreRes] = await Promise.all([
    fetchStaticNamespace(lng, ns),
    fetchFirestoreNamespace(lng, ns),
  ]);
  // merge with firestore overriding static keys
  return deepMerge(staticRes || {}, firestoreRes || {});
  })();

  namespaceRequestCache.set(cacheKey, request);
  return request;
};

// expose a small backend wrapper to integrate with i18next's `backend` option
const HybridBackend = {
  type: 'backend' as const,
  init: () => {
    console.log('[i18n] HybridBackend initialized');
  },
  read: async (lng: string, ns: string, callback: (err: any, data?: any) => void) => {
    try {
      // console.log(`[i18n] Loading namespace "${ns}" for language "${lng}"...`);
      const data = await loadNamespace(lng, ns);
      const keyCount = Object.keys(data).length;
      
      if (keyCount > 0) {
        // console.log(`[i18n] ✓ Loaded namespace "${ns}" for language "${lng}" (${keyCount} keys):`, Object.keys(data).slice(0, 10));
        callback(null, data);
      } else {
        console.warn(`[i18n] ⚠ Namespace "${ns}" for language "${lng}" is empty`);
        callback(null, {});
      }
    } catch (err) {
      // Not a component/hook - this is i18next's own backend `read()` callback, invoked by
      // the i18next library's internal namespace-loading machinery (including before React
      // ever mounts), so there is no ErrorProvider/React context available here. The failure
      // already propagates through i18next's own error-callback contract (`callback(err,
      // false)` below), which i18next uses for its own fallback/retry handling.
      console.error(`[i18n] ✗ Error loading namespace "${ns}" for language "${lng}":`, err);
      callback(err, false);
    }
  },
};

// initialize i18next with namespace support and our hybrid loader
i18n
  .use(HybridBackend as any)
  .use(detector)
  .use(initReactI18next)
  .init({
    debug: false,
    fallbackLng: 'en',
    // Preload commonly used namespaces
    ns: ['common'],
    defaultNS: 'common',
    // Allow loading additional namespaces on-demand
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    react: { 
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
    load: 'languageOnly',
    returnNull: false,
    returnEmptyString: false,
  });

// helper to allow explicit programmatic loading of namespaces with Firestore merge
export const ensureNamespace = async (namespace: string, lng?: string) => {
  const language = lng || i18n.language?.split?.('-')?.[0] || 'en';
  const data = await loadNamespace(language, namespace);
  if (data && Object.keys(data).length > 0) {
    i18n.addResourceBundle(language, namespace, data, true, true);
  }
  return data;
};

export default i18n;
